import { AutoComplete, Button, Input, Table, message } from 'antd'

import CopyIcon from '@assets/icons/CopySvg'
import { http } from '@services/http'
import { calculateKvm } from '@utils/calculateKvm'
import { calculateTotalWithKoef, calculateLineTotal } from '@utils/calculations'
import {
	formatNumberWithSpaces,
	numberWithSpacesIntl,
	parseLocalizedNumberInput,
} from '@utils/numberWithSpaces'
import {
	calculateShtPrices,
	calculateMetrPriceServices,
	calculateMetrPriceParametr,
	calculateKvmPrices,
} from '@utils/salesOrderCalculations'

const syncSelectedServicesQuantity = (services, quantity) => {
	return (services || []).map((service) => ({
		...service,
		itemQuantity: quantity,
		u_PlannedQuantity: quantity,
	}))
}

const SalesItemsTable = ({
	t,
	tableData,
	setTableData,
	itemsData,
	itemsLoading,
	whs,
	handleItemNameChange,
	setIsModalOpen,
	setCurrentRecord,
	setSelectedServices,
	addRow,
	copyRow,
	removeRow,
}) => {
	const columns = [
		{
			title: t('item'),
			dataIndex: 'itemCode',
			key: 'itemCode',
			fixed: 'left',
			width: 250,
			render: (_, record) => (
				<AutoComplete
					loading={itemsLoading}
					options={itemsData?.map((item) => ({
						value: item.itemCode,
						label: item.itemName,
						data: {
							ugpName: item.ugpName,
							price: item.price,
							koefitsent: item.u_DefaultCoefficient || 1,
							u_Number: item.u_Number,
							u_Timer: item.u_Timer,
							u_ProductionNumber: item.u_ProductionNumber,
						},
					}))}
					className="w-full "
					value={record.itemName}
					onSelect={async (value, label) => {
						if (!whs) {
							const updatedData = tableData.map((row) =>
								row.key === record.key
									? {
											...row,
											itemCode: '',
											itemName: '',
										}
									: row,
							)
							handleItemNameChange('')
							setTableData(updatedData)
							return message.error(
								'Склад не выбран, сначала выберите склад',
							)
						}

						const url = `api/items/inventory-items?itemCode=${value}`
						const { data } = await http.get(url).catch((error) => {
							message.error(error.response.data.message)
							return
						})
						const amountInWhs = data.data[0]?.inventoryItemWhs
							.filter((item) => item.whsCode == whs)
							.map((item) => item.onHand)

						const updatedData = tableData.map((row) =>
							row.key === record.key
								? {
										...row,
										itemCode: label.value,
										itemName: label.label || '',
										quantity: '',
										ugpName: label?.data?.ugpName || '',
										price: label?.data?.price || '',
										lineTotal: '',
										disabled: false,
										koefitsent:
											label?.data?.koefitsent || 1,
										u_Number: label?.data?.u_Number || '',
										u_Timer: label?.data?.u_Timer || '',
										u_ProductionNumber:
											label?.data?.u_ProductionNumber ||
											'',
										amountInWhs,
									}
								: row,
						)
						handleItemNameChange('')
						setTableData(updatedData)
					}}
					disabled={record.disabled}
					onChange={(val) => {
						const value = val || ''
						handleItemNameChange(value)
						const updatedData = tableData.map((row) =>
							row.key === record.key
								? {
										...row,
										itemName: value || '',
									}
								: row,
						)
						setTableData(updatedData)
					}}
					showSearch
				/>
			),
		},
		{
			title: `${t('width')} (${t('mm')})`,
			dataIndex: 'width',
			key: 'width',
			width: 150,
			render: (_, record) => (
				<Input
					value={formatNumberWithSpaces(record.width)}
					className={'w-full'}
					onChange={(e) => {
						const updatedData = tableData.map((row) => {
							if (row.key === record.key) {
								const rawInput = e.target.value

								const width = parseLocalizedNumberInput(rawInput)

								return {
									...row,
									width,
									selectedServices: syncSelectedServicesQuantity(
										row.selectedServices,
										row.quantity,
									),
								}
							}
							return row
						})
						setTableData(updatedData)
					}}
				/>
			),
		},
		{
			title: `${t('height')} (${t('mm')})`,
			dataIndex: 'height',
			key: 'height',
			width: 150,
			render: (_, record) => (
				<Input
					value={formatNumberWithSpaces(record.height)}
					className={'w-full'}
					onChange={(e) => {
						const updatedData = tableData.map((row) => {
							if (row.key === record.key) {
								const rawInput = e.target.value

								const height = parseLocalizedNumberInput(rawInput)

								return {
									...row,
									height,
									selectedServices: syncSelectedServicesQuantity(
										row.selectedServices,
										row.quantity,
									),
								}
							}
							return row
						})

						setTableData(updatedData)
					}}
				/>
			),
		},
		{
			title: t('quantity'),
			dataIndex: 'quantity',
			key: 'quantity',
			width: 150,
			render: (_, record) => (
				<Input
					value={formatNumberWithSpaces(record.quantity)}
					className="w-full"
					onChange={(e) => {
						const rawInput = e.target.value

						const updatedData = tableData.map((row) => {
							if (row.key === record.key) {
								const quantity = parseLocalizedNumberInput(rawInput)

								return {
									...row,
									quantity,
									selectedServices: syncSelectedServicesQuantity(
										row.selectedServices,
										quantity,
									),
								}
							}
							return row
						})

						setTableData(updatedData)
					}}
				/>
			),
		},
		{
			title: t('kv.m'),
			dataIndex: 'ugpName',
			key: 'ugpName',
			width: 150,
			render: (_, record) => {
				const koef = 1
				const totalWithKoef =
					calculateKvm(record.width, record.height) *
					parseFloat(record.quantity || 0) *
					koef

				// console.log(
				// 	'record = ',
				// 	record.width,
				// 	record.height,
				// 	calculateKvm(record.width, record.height),
				// )

				return (
					<span className="w-full">
						{numberWithSpacesIntl(totalWithKoef)}
					</span>
				)
			},
		},
		{
			title: t('pricePerKv'),
			dataIndex: 'price',
			key: 'price',
			width: 170,
			render: (_, record) => (
				<span className="w-full">
					{numberWithSpacesIntl(record.price || 0)}
				</span>
			),
		},
		// Uslugi modalga kirish bu joyda agar ugpName sht bolsa uni narxga qoshmaslik aks xolda uni userga usligi yozuvi orniga korastish kerak
		{
			title: t('services'),
			key: 'services',
			width: 120,
			render: (_, record) => {
				const servicesTotal = record.selectedServices
					? record.selectedServices.reduce((acc, service) => {
							if (
								service.ugpName !== 'шт' &&
								service.ugpName !== 'м' &&
								service.u_Modal !== 'Templete_Services' &&
								service.u_Modal !== 'ort' &&
								service.u_Modal !== 'Key_Sketches'
							) {
								return acc + service.price
							}
							return acc
						}, 0) *
							(record.quantity *
								((record.width * record.height) / 1000000) *
								1) +
						calculateShtPrices(record.selectedServices) +
						calculateMetrPriceServices(record?.selectedServices) +
						calculateMetrPriceParametr(record?.selectedServices)
					: 0
				return (
					<div className="flexw- full items-center justify-center">
						<Button
							type="primary"
							onClick={() => {
								setIsModalOpen(true)
								setCurrentRecord(record)
								setSelectedServices(
									record.selectedServices || [],
								)
								console.log(
									'record.selectedServices',
									record.selectedServices,
									record.selectedServices.reduce(
										(acc, service) => {
											if (
												service.ugpName !== 'шт' &&
												service.ugpName !== 'м' &&
												service.u_Modal !==
													'Templete_Services' &&
												service.u_Modal !== 'ort' &&
												service.u_Modal !==
													'Key_Sketches'
											) {
												return acc + service.price
											}
											return acc
										},
										0,
									) *
										(record.quantity *
											((record.width * record.height) /
												1000000) *
											1),
									calculateShtPrices(record.selectedServices),
									calculateMetrPriceServices(
										record?.selectedServices,
									),
									calculateMetrPriceParametr(
										record?.selectedServices,
									),
								)
							}}
						>
							{record.width > 0 ||
							record.height > 0 ||
							record.quantity > 0
								? numberWithSpacesIntl(servicesTotal)
								: t('services')}
						</Button>
					</div>
				)
			},
		},
		{
			title: t('totalMiqdor'),
			dataIndex: 'lineTotal',
			key: 'lineTotal',
			width: 170,
			render: (_, record) => {
				// console.log('record = ', index, record)
				const selectedPrices = calculateKvmPrices(
					record?.selectedServices,
				)
				const shtPrices = calculateShtPrices(record?.selectedServices)

				const koef = 1
				const totalWithKoef = calculateTotalWithKoef(
					record.width,
					record.height,
					record.quantity,
					koef,
				)

				const metrPricesServices = calculateMetrPriceServices(
					record?.selectedServices,
				)

				const metrPricesParametr = calculateMetrPriceParametr(
					record?.selectedServices,
				)

				const lineTotal =
					calculateLineTotal(
						record.price,
						selectedPrices,
						totalWithKoef,
					) +
					shtPrices +
					metrPricesServices +
					metrPricesParametr

				return <span>{numberWithSpacesIntl(lineTotal || 0)}</span>
			},
		},
		{
			title: t('actions'),
			key: 'actions',
			width: 100,

			render: (_, record) => (
				<div className="flex gap-2 items-center justify-center">
					<Button type="primary" onClick={addRow}>
						+
					</Button>
					<Button type="primary" onClick={() => copyRow(record)}>
						<CopyIcon />
					</Button>
					<Button
						danger
						type={'primary'}
						onClick={() => removeRow(record.key)}
						disabled={tableData.length === 1}
					>
						-
					</Button>
				</div>
			),
		},
	]

	return (
		<Table
			columns={columns}
			dataSource={tableData}
			pagination={false}
			className="border-1 mt-12 border-black  my-custom-table"
			bordered
			scroll={{ x: 'max-content' }}
		/>
	)
}

export default SalesItemsTable
